import { LANGUAGES, type Language } from '@lyrmo/shared';
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'lyrmo.pinned-languages';

function readStored(): Language[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(stored)
      ? LANGUAGES.filter((language) => language !== 'auto' && stored.includes(language))
      : [];
  } catch {
    return [];
  }
}

/** The languages kept at the top of the rewrite menu, remembered in this browser. */
export function usePinnedLanguages(): [readonly Language[], (language: Language) => void] {
  const [pinned, setPinned] = useState(readStored);

  useEffect(() => {
    try {
      if (pinned.length > 0) localStorage.setItem(STORAGE_KEY, JSON.stringify(pinned));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Pinning still works for this visit when storage is unavailable.
    }
  }, [pinned]);

  const toggle = useCallback((language: Language) => {
    if (language === 'auto') return;
    setPinned((current) =>
      current.includes(language)
        ? current.filter((kept) => kept !== language)
        : [...current, language],
    );
  }, []);

  return [pinned, toggle];
}
